import { useState } from 'react';
import { Link } from 'react-router-dom';
import { OptionChips } from '../../components/OptionChips';
import { isPlanError } from '../../components/plan/planErrors';
import { UpsellFromError } from '../../components/plan/Upsell';
import { TokenListEditor } from '../../components/TokenListEditor';
import type { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { PLATFORM_OPTIONS } from '../../lib/platforms';
import type { BusinessRead, BusinessUpdate, PlatformName } from '../../types/api';
import { knowledgeBaseSectionPath } from '../knowledge-base/sections';
import { updateBusiness } from '../onboarding/accountApi';
import { BRIEF_CAPS, presetGoalsFor, toPlatformNames } from '../onboarding/brandBrief';
import { brandKindCopy } from '../onboarding/brandKind';
import { useAccount } from '../onboarding/useAccount';

/** The brief as the form holds it: strings empty, never null. */
interface BrandValues {
  name: string;
  headline: string;
  industry: string;
  value_proposition: string;
  target_audience: string;
  platforms: PlatformName[];
  goals: string[];
}

function fromBusiness(business: BusinessRead): BrandValues {
  return {
    name: business.name,
    headline: business.headline ?? '',
    industry: business.industry ?? '',
    value_proposition: business.value_proposition ?? '',
    target_audience: business.target_audience ?? '',
    platforms: toPlatformNames(business.platforms),
    goals: [...business.goals],
  };
}

/** Every answer goes on the wire, trimmed; an emptied optional clears its column. */
function toUpdate(values: BrandValues, isPersonal: boolean): BusinessUpdate {
  const body: BusinessUpdate = {
    name: values.name.trim(),
    industry: values.industry.trim(),
    value_proposition: values.value_proposition.trim(),
    target_audience: values.target_audience.trim(),
    platforms: values.platforms,
    goals: values.goals,
  };
  if (isPersonal) body.headline = values.headline.trim();
  return body;
}

function toggle<T extends string>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

interface BrandSettingsFormProps {
  business: BusinessRead;
}

/**
 * The active brand's name and brief, editable afterwards (SRS 2.1).
 *
 * Saves through `PUT /businesses/{id}`. The name is the one field with teeth:
 * every prompt reads it, and the cached brand is replaced so the switcher and
 * the dashboard heading follow. Tone, tagline and the never-say list are
 * knowledge-base fields and are linked to, not duplicated here. A member who
 * is not the owner sees the form read-only, which is also what the database
 * would say.
 */
export function BrandSettingsForm({ business }: BrandSettingsFormProps) {
  const { setBusiness } = useAccount();
  const copy = brandKindCopy(business.kind);
  const isPersonal = business.kind === 'personal';
  const isOwner = business.role === 'owner';
  const presetGoals = presetGoalsFor(business.kind);

  const [values, setValues] = useState<BrandValues>(() => fromBusiness(business));
  const [savedFrom, setSavedFrom] = useState<BrandValues>(() => fromBusiness(business));
  const [error, setError] = useState<string | null>(null);
  // A 402: this brand is locked past the plan's allowance.
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty =
    JSON.stringify(toUpdate(values, isPersonal)) !==
    JSON.stringify(toUpdate(savedFrom, isPersonal));
  const customGoals = values.goals.filter((goal) => !presetGoals.includes(goal));

  function patch(changes: Partial<BrandValues>) {
    setError(null);
    setNotice(null);
    setValues((current) => ({ ...current, ...changes }));
  }

  async function handleSubmit() {
    const body = toUpdate(values, isPersonal);
    if (!body.name) {
      setError(isPersonal ? 'Add your name.' : 'The business needs a name.');
      return;
    }
    setError(null);
    setPlanError(null);
    setIsSaving(true);
    try {
      const result = await updateBusiness(business.id, body);
      setBusiness(result);
      setSavedFrom(fromBusiness(result));
      setValues(fromBusiness(result));
      setNotice('Saved.');
    } catch (caught) {
      if (isPlanError(caught)) setPlanError(caught);
      else setError(getErrorMessage(caught, 'Could not save. Try again.'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="wizard">
      <h2 className="wizard__title">
        {copy.kind === 'personal' ? 'Personal brand profile' : 'Business profile'}
      </h2>
      <form
        className="brief-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <p className="brief-form__kind">
          <span className="badge badge--neutral">{copy.tag}</span>
          {!isOwner ? (
            <span className="field__hint">Only the owner can edit this brand.</span>
          ) : null}
        </p>

        <fieldset className="brief-form__fields" disabled={!isOwner}>
          <div className="brief-form__row">
            <label className="field">
              <span className="field__label">
                {copy.name.label} <span className="brief-form__required">required</span>
              </span>
              <input
                className="field__input"
                type="text"
                maxLength={BRIEF_CAPS.business_name}
                autoComplete={isPersonal ? 'name' : 'organization'}
                value={values.name}
                onChange={(event) => patch({ name: event.target.value })}
              />
              <span className="field__hint">
                {isPersonal
                  ? 'Renames the brand everywhere, including prompts.'
                  : 'Renames the business everywhere, including prompts.'}
              </span>
            </label>

            {copy.headline ? (
              <label className="field">
                <span className="field__label">{copy.headline.label}</span>
                <input
                  className="field__input"
                  type="text"
                  maxLength={BRIEF_CAPS.headline}
                  placeholder={copy.headline.placeholder}
                  value={values.headline}
                  onChange={(event) => patch({ headline: event.target.value })}
                />
                <span className="field__hint">{copy.headline.hint}</span>
              </label>
            ) : null}

            <label className="field">
              <span className="field__label">{copy.industry.label}</span>
              <input
                className="field__input"
                type="text"
                maxLength={BRIEF_CAPS.industry}
                value={values.industry}
                onChange={(event) => patch({ industry: event.target.value })}
              />
              <span className="field__hint">{copy.industry.hint}</span>
            </label>
          </div>

          <label className="field">
            <span className="field__label">{copy.valueProposition.label}</span>
            <textarea
              className="field__input"
              rows={3}
              maxLength={BRIEF_CAPS.value_proposition}
              value={values.value_proposition}
              onChange={(event) => patch({ value_proposition: event.target.value })}
            />
            <span className="field__hint">
              Grounds every caption and the assistant. {values.value_proposition.length} of{' '}
              {BRIEF_CAPS.value_proposition}
            </span>
          </label>

          <label className="field">
            <span className="field__label">{copy.targetAudience.label}</span>
            <textarea
              className="field__input"
              rows={3}
              maxLength={BRIEF_CAPS.target_audience}
              value={values.target_audience}
              onChange={(event) => patch({ target_audience: event.target.value })}
            />
            <span className="field__hint">
              {values.target_audience.length} of {BRIEF_CAPS.target_audience}
            </span>
          </label>

          <OptionChips
            legend="Platforms"
            hint="Where the posts go. Each post still picks one."
            options={PLATFORM_OPTIONS}
            selected={values.platforms}
            onToggle={(value) =>
              patch({ platforms: toggle(values.platforms, value as PlatformName) })
            }
          />

          <div className="brief-form__goals">
            <OptionChips
              legend="Goals"
              options={copy.goals}
              selected={values.goals}
              onToggle={(value) => patch({ goals: toggle(values.goals, value) })}
            />
            <TokenListEditor
              label="Another goal"
              placeholder={copy.customGoalPlaceholder}
              addLabel="Add goal"
              tokens={customGoals}
              onChange={(tokens) =>
                patch({
                  goals: [...values.goals.filter((goal) => presetGoals.includes(goal)), ...tokens],
                })
              }
            />
          </div>
        </fieldset>

        <p className="field__hint">
          {copy.records.identity.tone}, {copy.records.identity.tagline.toLowerCase()} and the words
          to avoid live in the{' '}
          <Link to={knowledgeBaseSectionPath('brand-identity')}>brand profile</Link>.
        </p>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}
        <UpsellFromError error={planError} />
        {notice ? (
          <p className="form__notice" role="status">
            {notice}
          </p>
        ) : null}

        {isOwner ? (
          <div className="wizard__actions">
            <button
              className="button button--primary"
              type="submit"
              disabled={isSaving || !isDirty}
            >
              {isSaving ? 'Saving' : 'Save changes'}
            </button>
          </div>
        ) : null}
      </form>
    </section>
  );
}
